import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, MapPin, Mail, Loader2, Instagram, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

export const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/contact`, formData);
      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/#services', label: 'Services' },
    { href: '/tarifs', label: 'Tarifs' },
    { href: '/#contact', label: 'Contact' },
  ];

  return (
    <footer id="contact" data-testid="main-footer" className="bg-[#F5F5F7] pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <div>
            <img 
              src={LOGO_URL} 
              alt="WO Consulting" 
              className="h-20 w-auto object-contain mb-6"
              data-testid="footer-logo"
            />
            
            <div className="flex items-center gap-2 text-[#4A4A4A] mb-4">
              <MapPin size={18} />
              <span className="text-sm">BE <span className="swiss-flag">🇨🇭</span></span>
            </div>
            
            <div className="flex items-center gap-2 text-[#4A4A4A] mb-4">
              <Mail size={18} />
              <a 
                href="mailto:contact@woconsulting.net" 
                className="text-sm hover:text-[#0055FF] transition-colors"
                data-testid="footer-email"
              >
                contact@woconsulting.net
              </a>
            </div>

            <div className="flex items-center gap-2 text-[#4A4A4A] mb-4">
              <Instagram size={18} />
              <a 
                href="https://instagram.com/wo.consulting" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:text-[#0055FF] transition-colors"
                data-testid="footer-instagram"
              >
                @wo.consulting
              </a>
            </div>

            <div className="flex items-center gap-2 text-[#4A4A4A] mb-8">
              <Phone size={18} />
              <a 
                href="tel:+41798941692"
                className="text-sm hover:text-[#0055FF] transition-colors"
                data-testid="footer-phone"
              >
                +41 79 894 16 92
              </a>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <h3 className="font-['Manrope'] text-2xl font-bold text-[#111111] mb-6">
              Contactez-nous
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  data-testid="contact-name-input"
                  placeholder="Nom *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
                  required
                />
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
                  required
                />
              </div>
              <Input
                data-testid="contact-company-input"
                placeholder="Entreprise"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
              />
              <Textarea
                data-testid="contact-message-input"
                placeholder="Votre message *"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-white border-[#D0D0D0] focus:border-[#1a365d] min-h-[120px] rounded-lg resize-none"
                required
              />
              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1a365d] hover:bg-[#0f2440] text-white rounded-full h-12 text-sm font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-slate-700/20"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : (
                  <Send className="mr-2" size={18} />
                )}
                Envoyer le message
              </Button>
            </form>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-[#E5E5E5]">
          <p className="text-center text-sm text-[#4A4A4A]">
            © {new Date().getFullYear()} WO Consulting. Tous droits réservés. BE <span className="swiss-flag">🇨🇭</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
